"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useId } from "react";
import { cn } from "@/lib/utils";
import AQMark from "./AQMark";
import { generateLayeredGradients } from "@/lib/gradients";

/**
 * BackgroundPatternType defines the available background pattern options.
 */
export type BackgroundPatternType = "none" | "dots" | "grid" | "diagonal-lines" | "blueprint" | "primary-glow" | "soft-glow" | "organic-gradient" | "aq-watermark";

/**
 * Props for the BackgroundPattern component.
 */
interface BackgroundPatternProps {
  /** The type of pattern to render. */
  type?: BackgroundPatternType;
  /** The opacity of the pattern (0 to 1). */
  opacity?: number | null;
  /** Additional CSS classes. */
  className?: string;
  /** Whether to apply a radial mask to fade edges. Defaults to true. */
  masked?: boolean;
  /** Whether the background is dark (affects stroke/fill colors). */
  isDark?: boolean;
  /** Colors to use for gradient patterns (optional). */
  gradientColors?: string[];
}

/**
 * BackgroundPattern Component
 * 
 * Renders a variety of lightweight SVG background patterns or the brand watermark.
 * Includes a radial gradient mask to subtly fade patterns toward the edges.
 * 
 * @component
 */
export const BackgroundPattern = t0 => {
  const $ = _c(75);
  const {
    type: t1,
    opacity: t2,
    className,
    isDark: t3,
    masked: t4,
    gradientColors
  } = t0;
  const type = t1 === undefined ? "none" : t1;
  const opacity = t2 === undefined ? 0.03 : t2;
  const isDark = t3 === undefined ? true : t3;
  const masked = t4 === undefined ? true : t4;
  const id = useId();
  const finalOpacity = opacity ?? 0.03;
  const strokeColor = isDark ? "white" : "black";
  const fillColor = isDark ? "white" : "black";
  if (type === "none") {
    return null;
  }
  if (type === "aq-watermark") {
    const t5 = isDark ? "invert" : "";
    let t6;
    if ($[0] !== className || $[1] !== t5) {
      t6 = cn("absolute left-1/2 top-1/2 z-0 size-[var(--aq-width-content-wide)] -translate-x-1/2 -translate-y-1/2 rotate-12 pointer-events-none grayscale", t5, className);
      $[0] = className;
      $[1] = t5;
      $[2] = t6;
    } else {
      t6 = $[2];
    }
    let t7;
    if ($[3] !== finalOpacity) {
      t7 = {
        opacity: finalOpacity
      };
      $[3] = finalOpacity;
      $[4] = t7;
    } else {
      t7 = $[4];
    }
    let t8;
    if ($[5] !== t6 || $[6] !== t7) {
      t8 = <AQMark className={t6} style={t7} />;
      $[5] = t6;
      $[6] = t7;
      $[7] = t8;
    } else {
      t8 = $[7];
    }
    return t8;
  }
  if (type === "blueprint") {
    let t5;
    if ($[8] !== className) {
      t5 = cn("absolute inset-0 z-0 pointer-events-none", className);
      $[8] = className;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== finalOpacity) {
      t6 = {
        opacity: finalOpacity,
        backgroundImage: "\n                        repeating-linear-gradient(45deg, transparent, transparent 32px, rgba(0,0,0,0.1) 32px, rgba(0,0,0,0.1) 33px),\n                        repeating-linear-gradient(135deg, transparent, transparent 32px, rgba(0,0,0,0.1) 32px, rgba(0,0,0,0.1) 33px)\n                    "
      };
      $[10] = finalOpacity;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== t5 || $[13] !== t6) {
      t7 = <div className={t5} style={t6} />;
      $[12] = t5;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  if (type === "primary-glow") {
    let t5;
    if ($[15] !== className) {
      t5 = cn("absolute inset-0 z-0 pointer-events-none", className);
      $[15] = className;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== finalOpacity) {
      t6 = {
        opacity: finalOpacity,
        backgroundImage: "\n                        radial-gradient(circle 600px at 0% 200px, oklch(from var(--primary, #f26649) calc(l * 0.7) calc(c * 0.6) h), transparent),\n                        radial-gradient(circle 600px at 100% 200px, oklch(from var(--primary, #f26649) calc(l * 0.75) calc(c * 0.65) h), transparent)\n                    "
      };
      $[17] = finalOpacity;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== t5 || $[20] !== t6) {
      t7 = <div className={t5} style={t6} />;
      $[19] = t5;
      $[20] = t6;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  if (type === "soft-glow") {
    let t5;
    if ($[22] !== className) {
      t5 = cn("absolute inset-0 z-0 pointer-events-none", className);
      $[22] = className;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    let t6;
    if ($[24] !== finalOpacity) {
      t6 = {
        opacity: finalOpacity,
        background: "radial-gradient(125% 125% at 50% 10%, transparent 40%, var(--primary, #f26649) 100%)"
      };
      $[24] = finalOpacity;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    let t7;
    if ($[26] !== t5 || $[27] !== t6) {
      t7 = <div className={t5} style={t6} />;
      $[26] = t5;
      $[27] = t6;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    return t7;
  }
  if (type === "organic-gradient") {
    let t5;
    if ($[29] !== gradientColors) {
      t5 = generateLayeredGradients(gradientColors);
      $[29] = gradientColors;
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    const gradientStyle = t5;
    let t6;
    if ($[31] !== className) {
      t6 = cn("absolute inset-0 z-0 pointer-events-none", className);
      $[31] = className;
      $[32] = t6;
    } else {
      t6 = $[32];
    }
    let t7;
    if ($[33] !== gradientStyle) {
      t7 = {
        opacity: 1,
        backgroundImage: gradientStyle
      };
      $[33] = gradientStyle;
      $[34] = t7;
    } else {
      t7 = $[34];
    }
    let t8;
    if ($[35] !== t6 || $[36] !== t7) {
      t8 = <div className={t6} style={t7} />;
      $[35] = t6;
      $[36] = t7;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    return t8;
  }
  const t5 = isDark ? "stroke-white fill-white" : "stroke-black fill-black";
  let t6;
  if ($[38] !== className || $[39] !== t5) {
    t6 = cn("pointer-events-none absolute inset-0 h-full w-full z-0", t5, className);
    $[38] = className;
    $[39] = t5;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  let t7;
  if ($[41] !== finalOpacity) {
    t7 = {
      opacity: finalOpacity
    };
    $[41] = finalOpacity;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  if ($[43] !== fillColor || $[44] !== id || $[45] !== type) {
    t8 = type === "dots" && <pattern id={id} width={24} height={24} patternUnits="userSpaceOnUse"><circle cx={1.5} cy={1.5} r={1.5} fill={fillColor} /></pattern>;
    $[43] = fillColor;
    $[44] = id;
    $[45] = type;
    $[46] = t8;
  } else {
    t8 = $[46];
  }
  let t9;
  if ($[47] !== id || $[48] !== strokeColor || $[49] !== type) {
    t9 = type === "grid" && <pattern id={id} width={40} height={40} patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke={strokeColor} strokeWidth={1} /></pattern>;
    $[47] = id;
    $[48] = strokeColor;
    $[49] = type;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  let t10;
  if ($[51] !== id || $[52] !== strokeColor || $[53] !== type) {
    t10 = type === "diagonal-lines" && <pattern id={id} width={10} height={10} patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1={0} y1={0} x2={0} y2={10} stroke={strokeColor} strokeWidth={1} fill="none" /></pattern>;
    $[51] = id;
    $[52] = strokeColor;
    $[53] = type;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  let t11;
  if ($[55] !== id || $[56] !== masked) {
    t11 = masked && <mask id={`${id}-mask`}><rect width="100%" height="100%" fill={`url(#${id}-gradient)`} /></mask>;
    $[55] = id;
    $[56] = masked;
    $[57] = t11;
  } else {
    t11 = $[57];
  }
  let t12;
  if ($[58] !== id || $[59] !== masked) {
    t12 = masked && <radialGradient id={`${id}-gradient`} cx="50%" cy="50%" r="100%"><stop offset="40%" stopColor="white" /><stop offset="100%" stopColor="transparent" /></radialGradient>;
    $[58] = id;
    $[59] = masked;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  let t13;
  if ($[61] !== t10 || $[62] !== t11 || $[63] !== t12 || $[64] !== t8 || $[65] !== t9) {
    t13 = <defs>{t8}{t9}{t10}{t11}{t12}</defs>;
    $[61] = t10;
    $[62] = t11;
    $[63] = t12;
    $[64] = t8;
    $[65] = t9;
    $[66] = t13;
  } else {
    t13 = $[66];
  }
  const t14 = `url(#${id})`;
  const t15 = masked ? `url(#${id}-mask)` : undefined;
  let t16;
  if ($[67] !== t14 || $[68] !== t15) {
    t16 = <rect width="100%" height="100%" fill={t14} mask={t15} />;
    $[67] = t14;
    $[68] = t15;
    $[69] = t16;
  } else {
    t16 = $[69];
  }
  let t17;
  if ($[70] !== t13 || $[71] !== t16 || $[72] !== t6 || $[73] !== t7) {
    t17 = <svg aria-hidden="true" className={t6} style={t7}>{t13}{t16}</svg>;
    $[70] = t13;
    $[71] = t16;
    $[72] = t6;
    $[73] = t7;
    $[74] = t17;
  } else {
    t17 = $[74];
  }
  return t17;
};